import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Lock, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";

// action -> the DIRECT permission that lets a user do it without approval.
export const APPROVAL_ACTIONS = {
  "customers.delete": { label: "Delete customer", perm: "customers.delete" },
  "pets.delete": { label: "Delete pet", perm: "pets.delete" },
  "bookings.delete": { label: "Delete booking", perm: "bookings.delete" },
  "package_plans.delete": { label: "Delete package plan", perm: "services.delete" },
  "inventory.delete": { label: "Delete inventory item", perm: "inventory.delete" },
  "invoices.cancel": { label: "Cancel invoice", perm: "invoices.edit" },
  "payments.refund": { label: "Refund payment", perm: "payments.refund" },
};

/* Maker-side control. Renders the "Owner approval required" request flow ONLY for a user who
   (a) lacks the direct permission for this action, and (b) holds approval.create. Users with the
   direct permission (owner/admin/manager) see nothing here — they act through their normal
   direct buttons. The mutation is NEVER performed here; it only files a pending request. */
export function ApprovalRequestButton({ action, targetId, targetLabel, className = "", size = "sm" }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const meta = APPROVAL_ACTIONS[action];
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const eligible = !!meta && !hasPerm(user, meta.perm) && hasPerm(user, "approval.create");

  // Detect an existing pending request for this exact target+action (backend remains authoritative).
  const { data: existingList } = useQuery({
    queryKey: ["my-pending-approvals", action],
    queryFn: async () => (await api.get(`/approvals?action=${action}&limit=200`)).data,
    enabled: eligible && open,
  });
  const mine = (existingList?.items || []).filter((r) => r.target_id === targetId);
  const existing = mine.find((r) => r.status === "pending");
  const last = !existing ? mine[0] : null;

  useEffect(() => { if (open) { setReason(""); setSent(null); } }, [open]);

  const policyOn = (user?.tenant?.approval_policy || {})[action] !== false;
  if (!eligible || !policyOn) return null;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/approvals", { action, target_id: targetId, reason: reason.trim() });
      setSent(data);
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["my-pending-approvals", action] });
      toast.success("Approval request sent to the business owner.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const shown = sent || existing;

  return (
    <>
      <Button size={size} variant="outline" onClick={() => setOpen(true)}
        data-testid={`request-approval-${action}-${targetId}`}
        className={`gap-1.5 text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100 ${className}`}>
        <Lock className="h-3.5 w-3.5" /> Owner approval required
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-sm" data-testid="approval-request-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-500" /> Owner Approval Required
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-xl border border-pawkt-border bg-pawkt-bg p-3 text-sm space-y-1">
            <div className="flex justify-between gap-2"><span className="text-slate-500">Action</span><b className="text-slate-900">{meta.label}</b></div>
            <div className="flex justify-between gap-2"><span className="text-slate-500">Item</span><b className="text-slate-900 truncate max-w-[60%] text-right">{targetLabel}</b></div>
          </div>

          {shown ? (
            <div className="space-y-2" data-testid="approval-request-pending">
              <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                <Clock className="h-4 w-4" />
                {existing && !sent ? "An approval request for this action is already pending." : "Approval request sent to the business owner."}
              </div>
              <div className="text-xs text-slate-500">
                Status: <b className="text-slate-700">Pending</b> · Request ID: <span className="font-mono">{shown.id}</span>
                <div className="mt-0.5">The owner will review it. This record is unchanged until approved.</div>
              </div>
              <DialogFooter><Button onClick={() => setOpen(false)} data-testid="approval-request-close" className="min-h-[44px]">Close</Button></DialogFooter>
            </div>
          ) : (
            <>
              {last && (
                <div data-testid="approval-request-last-status" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  Last request: <b className="capitalize text-slate-800">{last.status}</b>
                  {last.status === "rejected" && last.rejected_by_name ? ` by ${last.rejected_by_name}` : ""}
                  {last.rejection_reason ? ` · "${last.rejection_reason}"` : ""}
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Reason (optional)</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="approval-request-reason"
                  placeholder="e.g. Duplicate record" rows={3} className="bg-white" />
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} className="min-h-[44px]">Cancel</Button>
                <Button onClick={send} disabled={busy} data-testid="approval-request-submit"
                  className="min-h-[44px] bg-amber-600 hover:bg-amber-700 text-white">
                  {busy ? "Sending…" : "Send for Approval"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
